// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/NewPatientRequests/NewPatientRequestsPage.tsx

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./NewPatientRequestsPage.css";

import { downloadReportPdf } from "../../../../utils/reportExport";
import type { Report } from "../../../../utils/reportExport";

import {
  clinicAdminApi,
  FamilyDoctorListItem,
  ClinicAdminPatientRequestListItem,
  ApiError,
} from "../../../../api/clinicAdmin";
import { formatDateTime } from "../../../../utils/clinicAdminHelpers";
import { Skeleton } from "../components/Skeleton";

type LoadState = "idle" | "loading" | "ready" | "error";

type PatientBaselineFilter = "all" | "unattached" | "attached";

type SourceFilter = "all" | "public" | "navigator";

type StatusGroupFilter =
  | "all"
  | "submittedReview"
  | "waitlisted"
  | "invited"
  | "attached"
  | "closed";

type SortKey =
  | "requestId"
  | "name"
  | "submitted"
  | "doctor"
  | "source"
  | "patientBaseline"
  | "priority"
  | "currentStatus"
  | "attachmentDate"
  | "tta";

type SortDirection = "asc" | "desc";

type SortState = {
  key: SortKey;
  direction: SortDirection;
};

type StatusCounts = Record<StatusGroupFilter, number>;

type PatientBaselineKey = "unattached" | "attached" | "unknown";

type PaginationToken = number | "ellipsis";

const PAGE_SIZE = 20;

const CLOSED_STATUS_KEYS = new Set([
  "declined",
  "closedpatientwithdrew",
  "closedacceptedelsewhere",
  "closednoshow",
  "closedclinicdeclinedaftermeetgreet",
  "cancelled",
  "expired",
]);

function asText(value: unknown): string {
  return String(value ?? "").trim();
}

function asLower(value: unknown): string {
  return asText(value).toLowerCase();
}

function toTimestamp(value: unknown): number | null {
  const raw = asText(value);
  if (!raw) return null;

  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : null;
}

function formatDate(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function normalizeStatus(status: unknown): string {
  const raw = asText(status);
  if (!raw) return "";

  const key = raw.toLowerCase();

  if (key === "in_review") return "InReview";
  if (key === "notaccepted") return "Declined";

  if (key === "attachment_complete" || key === "attachmentcomplete") {
    return "AttachmentComplete";
  }

  if (key === "closedpatientwithdrew") return "ClosedPatientWithdrew";
  if (key === "closedacceptedelsewhere") return "ClosedAcceptedElsewhere";
  if (key === "closednoshow") return "ClosedNoShow";

  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "ClosedClinicDeclinedAfterMeetGreet";
  }

  return raw;
}

function statusForItem(item: ClinicAdminPatientRequestListItem): string {
  const normalized = normalizeStatus(
    (item as any)?.statusName ||
      (item as any)?.status ||
      (item as any)?.rawStatusName ||
      (item as any)?.rawStatus
  );

  const key = normalized.toLowerCase();
  const hasAttachmentCompleted = !!attachmentCompletedAt(item);

  if (key === "accepted" && hasAttachmentCompleted) {
    return "AttachmentComplete";
  }

  return normalized;
}

function currentStatusLabel(status: unknown): string {
  const normalized = normalizeStatus(status);
  const key = normalized.toLowerCase();

  if (key === "submitted") return "Submitted";
  if (key === "inreview") return "Under Review";
  if (key === "waitlist") return "Waitlisted";
  if (key === "accepted") return "Invited";
  if (key === "attachmentcomplete") return "Attached";
  if (key === "declined") return "Declined";
  if (key === "closedpatientwithdrew") return "Closed — Patient Withdrew";
  if (key === "closedacceptedelsewhere") return "Closed — Accepted Elsewhere";
  if (key === "closednoshow") return "Closed — No Show";

  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "Closed — Clinic Declined After Meet & Greet";
  }

  if (key === "cancelled") return "Cancelled";
  if (key === "expired") return "Expired";

  return normalized || "Unknown";
}

function statusGroupForRequest(
  item: ClinicAdminPatientRequestListItem
): StatusGroupFilter {
  const statusKey = statusForItem(item).toLowerCase();

  if (statusKey === "submitted" || statusKey === "inreview") {
    return "submittedReview";
  }

  if (statusKey === "waitlist") return "waitlisted";
  if (statusKey === "accepted") return "invited";
  if (statusKey === "attachmentcomplete") return "attached";
  if (CLOSED_STATUS_KEYS.has(statusKey)) return "closed";

  return "submittedReview";
}

function statusGroupLabel(filter: StatusGroupFilter): string {
  if (filter === "all") return "All";
  if (filter === "submittedReview") return "Submitted";
  if (filter === "waitlisted") return "Waitlisted";
  if (filter === "invited") return "Invited";
  if (filter === "attached") return "Attached";
  if (filter === "closed") return "Closed";
  return "All";
}

function statusPillClass(status: unknown): string {
  const key = normalizeStatus(status).toLowerCase();

  if (key === "submitted" || key === "inreview") {
    return "ocf-status ocf-status--pending";
  }

  if (key === "waitlist") return "ocf-status ocf-status--waitlist";
  if (key === "accepted") return "ocf-status ocf-status--accepted";
  if (key === "attachmentcomplete") return "ocf-status ocf-status--accepted";

  if (key === "declined") return "ocf-status ocf-status--declined";

  if (CLOSED_STATUS_KEYS.has(key)) {
    return "ocf-status ocf-status--muted";
  }

  return "ocf-status";
}

function patientDisplayName(patient: any): string {
  if (!patient) return "Unnamed patient";

  const first = asText(patient.firstName);
  const last = asText(patient.lastName);
  const composed = [first, last].filter(Boolean).join(" ").trim();

  if (composed) return composed;

  const legacy = asText(patient.fullName || patient.name);
  return legacy || "Unnamed patient";
}

function patientPostalCode(item: ClinicAdminPatientRequestListItem): string {
  return (
    asText((item as any)?.patient?.postalCode) ||
    asText((item as any)?.postalCode) ||
    "—"
  );
}

function submittedAt(item: ClinicAdminPatientRequestListItem): string | null {
  const timeline = (item as any)?.timeline || {};

  return (
    asText(timeline.submittedAt) ||
    asText(timeline.createdAt) ||
    asText((item as any)?.submittedAt) ||
    asText((item as any)?.createdAt) ||
    null
  );
}

function attachmentCompletedAt(
  item: ClinicAdminPatientRequestListItem
): string | null {
  const timeline = (item as any)?.timeline || {};

  return (
    asText(timeline.attachmentCompletedAt) ||
    asText((item as any)?.attachmentCompletedAt) ||
    null
  );
}

function ttaDays(item: ClinicAdminPatientRequestListItem): number | null {
  const start = toTimestamp(submittedAt(item));
  const end = toTimestamp(attachmentCompletedAt(item));

  if (start === null || end === null) return null;
  if (end < start) return null;

  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return Math.max(days, 0);
}

function formatTta(item: ClinicAdminPatientRequestListItem): string {
  const days = ttaDays(item);
  if (days === null) return "—";
  if (days === 0) return "Same day";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function hasPriorityTag(
  item: ClinicAdminPatientRequestListItem,
  key: string
): boolean {
  const wanted = key.toLowerCase();
  const tags = Array.isArray((item as any)?.priorityTags)
    ? (item as any).priorityTags
    : [];

  return tags.some((tag: any) => {
    const tagKey = asLower(tag?.key);
    const tagLabel = asLower(tag?.label);
    return tagKey === wanted || tagLabel === wanted;
  });
}

function patientBaselineKey(
  item: ClinicAdminPatientRequestListItem
): PatientBaselineKey {
  const baseline = asLower((item as any)?.patientStatusBeforeRequest);

  if (baseline === "unattached") return "unattached";
  if (baseline === "attached") return "attached";

  if (
    hasPriorityTag(item, "unattached_moh") ||
    hasPriorityTag(item, "unattached")
  ) {
    return "unattached";
  }

  if (hasPriorityTag(item, "attached_moh") || hasPriorityTag(item, "attached")) {
    return "attached";
  }

  return "unknown";
}

function patientBaselineLabel(item: ClinicAdminPatientRequestListItem): string {
  const baseline = patientBaselineKey(item);

  if (baseline === "unattached") return "Unattached";
  if (baseline === "attached") return "Attached / Switching";

  return "Unknown";
}

function patientBaselineFilterLabel(filter: PatientBaselineFilter): string {
  if (filter === "unattached") return "Unattached";
  if (filter === "attached") return "Attached / Switching";
  return "All";
}

function patientBaselineSortValue(item: ClinicAdminPatientRequestListItem) {
  const baseline = patientBaselineKey(item);

  if (baseline === "unattached") return 1;
  if (baseline === "attached") return 2;

  return 3;
}

function patientBaselineClass(item: ClinicAdminPatientRequestListItem): string {
  const baseline = patientBaselineKey(item);

  if (baseline === "unattached") {
    return "ocf-baseline-pill ocf-baseline-pill--unattached";
  }

  if (baseline === "attached") {
    return "ocf-baseline-pill ocf-baseline-pill--attached";
  }

  return "ocf-baseline-pill ocf-baseline-pill--unknown";
}

function isNavigatorSourceType(value: unknown): boolean {
  const sourceType = asText(value);

  return (
    sourceType === "NavigatorPublicRequest" ||
    sourceType === "NavigatorUpload" ||
    sourceType === "OHT" ||
    sourceType === "FutureHCC" ||
    sourceType === "ManualNavigatorEntry"
  );
}

function isNavigatorRequest(item: ClinicAdminPatientRequestListItem): boolean {
  const anyItem = item as any;
  const navigator = anyItem.navigator;

  if (navigator?.isNavigatorAssisted) return true;

  if (
    anyItem.navigatorCaseId !== null &&
    anyItem.navigatorCaseId !== undefined
  ) {
    return true;
  }

  return (
    isNavigatorSourceType(anyItem.sourceType) ||
    isNavigatorSourceType(anyItem.SourceType) ||
    asLower(anyItem.source) === "navigator"
  );
}

function sourceKey(item: ClinicAdminPatientRequestListItem): SourceFilter {
  return isNavigatorRequest(item) ? "navigator" : "public";
}

function sourceLabel(item: ClinicAdminPatientRequestListItem): string {
  return isNavigatorRequest(item) ? "Navigator" : "Public";
}

function sourceFilterLabel(filter: SourceFilter): string {
  if (filter === "navigator") return "Navigator";
  if (filter === "public") return "Public";
  return "All";
}

function sourceClass(item: ClinicAdminPatientRequestListItem): string {
  return isNavigatorRequest(item)
    ? "ocf-source-pill ocf-source-pill--navigator"
    : "ocf-source-pill ocf-source-pill--public";
}

function sourceMatchesFilter(
  item: ClinicAdminPatientRequestListItem,
  sourceFilter: SourceFilter
): boolean {
  if (sourceFilter === "all") return true;
  return sourceKey(item) === sourceFilter;
}

function normalizePriorityLabel(value: unknown): string | null {
  const raw = asText(value);
  if (!raw) return null;

  const compact = raw.toLowerCase().replace(/[\s_-]+/g, "");

  if (compact === "urgent" || compact.includes("priorityurgent")) {
    return "Urgent";
  }

  if (compact === "high" || compact.includes("priorityhigh")) {
    return "High";
  }

  if (
    compact === "medium" ||
    compact === "moderate" ||
    compact.includes("prioritymedium") ||
    compact.includes("prioritymoderate")
  ) {
    return "Medium";
  }

  if (compact === "low" || compact.includes("prioritylow")) {
    return "Low";
  }

  if (
    compact === "routine" ||
    compact === "standard" ||
    compact.includes("priorityroutine") ||
    compact.includes("prioritystandard")
  ) {
    return "Routine";
  }

  return null;
}

function priorityRawValue(item: ClinicAdminPatientRequestListItem): string {
  const anyItem = item as any;

  const directCandidates = [
    anyItem.priorityLevel,
    anyItem.PriorityLevel,
    anyItem.priority,
    anyItem.Priority,
    anyItem.request?.priorityLevel,
    anyItem.request?.PriorityLevel,
    anyItem.request?.priority,
    anyItem.request?.Priority,
  ];

  for (const candidate of directCandidates) {
    const normalized = normalizePriorityLabel(candidate);
    if (normalized) return normalized;
  }

  const priorityTags = Array.isArray(anyItem.priorityTags)
    ? anyItem.priorityTags
    : [];

  for (const tag of priorityTags) {
    const normalized =
      normalizePriorityLabel(tag?.key) ||
      normalizePriorityLabel(tag?.label) ||
      normalizePriorityLabel(tag?.value);

    if (normalized) return normalized;
  }

  return "";
}

function priorityLabel(item: ClinicAdminPatientRequestListItem): string {
  const raw = priorityRawValue(item);

  return raw || "Routine";
}

function prioritySortValue(item: ClinicAdminPatientRequestListItem): number {
  const label = priorityLabel(item).toLowerCase();

  if (label === "urgent") return 1;
  if (label === "high") return 2;
  if (label === "medium") return 3;
  if (label === "routine") return 4;
  if (label === "low") return 5;

  return 6;
}

function priorityClass(item: ClinicAdminPatientRequestListItem): string {
  const label = priorityLabel(item).toLowerCase();

  if (label === "urgent") return "ocf-priority-pill ocf-priority-pill--urgent";
  if (label === "high") return "ocf-priority-pill ocf-priority-pill--high";
  if (label === "medium") return "ocf-priority-pill ocf-priority-pill--medium";
  if (label === "low") return "ocf-priority-pill ocf-priority-pill--low";

  return "ocf-priority-pill ocf-priority-pill--routine";
}

function baselineMatchesFilter(
  item: ClinicAdminPatientRequestListItem,
  baselineFilter: PatientBaselineFilter
): boolean {
  if (baselineFilter === "all") return true;
  return patientBaselineKey(item) === baselineFilter;
}

function statusMatchesFilter(
  item: ClinicAdminPatientRequestListItem,
  statusFilter: StatusGroupFilter
): boolean {
  if (statusFilter === "all") return true;
  return statusGroupForRequest(item) === statusFilter;
}

function requestMatchesSearch(
  item: ClinicAdminPatientRequestListItem,
  search: string
): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;

  const patientName = patientDisplayName((item as any).patient);
  const firstName = asText((item as any).patient?.firstName);
  const lastName = asText((item as any).patient?.lastName);
  const postalCode = patientPostalCode(item);
  const requestId = String(item.requestId || "");
  const doctorName = asText((item as any)?.familyDoctorName);
  const priority = priorityLabel(item);
  const source = sourceLabel(item);

  const searchableText = [
    patientName,
    firstName,
    lastName,
    postalCode,
    requestId,
    doctorName,
    priority,
    source,
  ]
    .map(asText)
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchableText.includes(q);
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function compareNullableNumber(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

function getSortValue(
  item: ClinicAdminPatientRequestListItem,
  key: SortKey
): string | number | null {
  if (key === "requestId") return Number(item.requestId) || 0;
  if (key === "name") return patientDisplayName((item as any).patient);
  if (key === "submitted") return toTimestamp(submittedAt(item));
  if (key === "doctor") return asText((item as any)?.familyDoctorName);
  if (key === "source") return sourceLabel(item);
  if (key === "patientBaseline") return patientBaselineSortValue(item);
  if (key === "priority") return prioritySortValue(item);
  if (key === "currentStatus") return currentStatusLabel(statusForItem(item));
  if (key === "attachmentDate") return toTimestamp(attachmentCompletedAt(item));
  if (key === "tta") return ttaDays(item);

  return null;
}

function sortRequests(
  list: ClinicAdminPatientRequestListItem[],
  sortState: SortState
): ClinicAdminPatientRequestListItem[] {
  const sorted = [...list];

  sorted.sort((a, b) => {
    const aValue = getSortValue(a, sortState.key);
    const bValue = getSortValue(b, sortState.key);

    let result = 0;

    if (typeof aValue === "string" || typeof bValue === "string") {
      result = compareText(asText(aValue), asText(bValue));
    } else {
      result = compareNullableNumber(
        typeof aValue === "number" ? aValue : null,
        typeof bValue === "number" ? bValue : null
      );
    }

    if (result === 0) {
      result = Number(a.requestId || 0) - Number(b.requestId || 0);
    }

    return sortState.direction === "asc" ? result : -result;
  });

  return sorted;
}

function sortButtonLabel(label: string, key: SortKey, sortState: SortState) {
  if (sortState.key !== key) return label;
  return `${label} ${sortState.direction === "asc" ? "↑" : "↓"}`;
}

function exportFilename(): string {
  const stamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[-:T]/g, "");

  return `clinic-admin-patient-requests-${stamp}`;
}

function buildRequestsExportReport({
  rows,
  baselineFilter,
  sourceFilter,
  statusFilter,
  search,
  selectedDoctorLabel,
  showDoctorColumn,
}: {
  rows: ClinicAdminPatientRequestListItem[];
  baselineFilter: PatientBaselineFilter;
  sourceFilter: SourceFilter;
  statusFilter: StatusGroupFilter;
  search: string;
  selectedDoctorLabel: string;
  showDoctorColumn: boolean;
}): Report {
  const generatedAt = new Date().toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const filterSummary = [
    `Doctor: ${selectedDoctorLabel}`,
    `Source: ${sourceFilterLabel(sourceFilter)}`,
    `Patient Baseline: ${patientBaselineFilterLabel(baselineFilter)}`,
    `Status: ${statusGroupLabel(statusFilter)}`,
    search.trim() ? `Search: “${search.trim()}”` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const sourceCounts = rows.reduce(
    (counts, item) => {
      if (isNavigatorRequest(item)) {
        counts.navigator += 1;
      } else {
        counts.public += 1;
      }

      return counts;
    },
    { public: 0, navigator: 0 }
  );

  const baselineCounts = rows.reduce(
    (counts, item) => {
      const baseline = patientBaselineKey(item);
      if (baseline === "unattached") counts.unattached += 1;
      if (baseline === "attached") counts.attached += 1;
      if (baseline === "unknown") counts.unknown += 1;
      return counts;
    },
    { unattached: 0, attached: 0, unknown: 0 }
  );

  const priorityCounts = rows.reduce((counts, item) => {
    const label = priorityLabel(item);
    counts[label] = (counts[label] || 0) + 1;
    return counts;
  }, {} as Record<string, number>);

  const requestColumns = [
    "Request #",
    "Name",
    "Postal Code",
    "Submitted",
    ...(showDoctorColumn ? ["Doctor"] : []),
    "Source",
    "Patient Baseline",
    "Priority",
    "Current Status",
    "Attachment Date",
    "TTA",
  ];

  const requestColWeights = [
    0.8,
    1.7,
    1,
    1.1,
    ...(showDoctorColumn ? [1.5] : []),
    0.95,
    1.35,
    0.9,
    1.5,
    1.2,
    0.75,
  ];

  const requestRows = rows.map((item) => {
    const submitted = submittedAt(item);
    const attachedAt = attachmentCompletedAt(item);
    const itemStatus = statusForItem(item);

    return [
      `#${item.requestId}`,
      patientDisplayName((item as any).patient),
      patientPostalCode(item),
      formatDate(submitted),
      ...(showDoctorColumn ? [asText((item as any)?.familyDoctorName) || "—"] : []),
      sourceLabel(item),
      patientBaselineLabel(item),
      priorityLabel(item),
      currentStatusLabel(itemStatus),
      formatDate(attachedAt),
      formatTta(item),
    ];
  });

  return {
    pageLabel: "Clinic Admin Portal — Patient Requests",
    subtitle: `${filterSummary} · Generated ${generatedAt}`,
    chapters: [
      {
        title: "Export Summary",
        summary:
          "This PDF includes the full current filtered request selection from the Clinic Admin Portal. Pagination affects only the on-screen table, not this export.",
        sections: [
          {
            type: "kpi",
            heading: "Selection Overview",
            items: [
              {
                label: "Requests exported",
                value: String(rows.length),
                hint: "Full filtered selection",
                tone: "blue",
              },
              {
                label: "Public requests",
                value: String(sourceCounts.public),
                hint: "Direct patient submissions",
                tone: "green",
              },
              {
                label: "Navigator requests",
                value: String(sourceCounts.navigator),
                hint: "Navigator-assisted submissions",
                tone: "amber",
              },
              {
                label: "Unattached baseline",
                value: String(baselineCounts.unattached),
                hint: "MOH-style unattached reason",
                tone: "grey",
              },
            ],
          },
          {
            type: "table",
            heading: "Applied Filters",
            columns: ["Filter", "Value"],
            rows: [
              ["Doctor", selectedDoctorLabel],
              ["Source", sourceFilterLabel(sourceFilter)],
              ["Patient Baseline", patientBaselineFilterLabel(baselineFilter)],
              ["Status", statusGroupLabel(statusFilter)],
              ["Search", search.trim() || "—"],
              ["Generated", generatedAt],
            ],
            colWeights: [1, 3],
          },
          {
            type: "table",
            heading: "Priority Breakdown",
            columns: ["Priority", "Requests"],
            rows: Object.entries(priorityCounts)
              .sort(([a], [b]) => compareText(a, b))
              .map(([priority, count]) => [priority, String(count)]),
            aligns: ["left", "right"],
            colWeights: [2, 1],
          },
        ],
      },
      {
        title: "Patient Requests",
        summary:
          rows.length > 0
            ? "Rows are shown in the same filtered and sorted order as the on-screen list."
            : "No requests matched the current selection.",
        sections: [
          {
            type: "table",
            heading: "Request List",
            columns: requestColumns,
            rows: requestRows,
            colWeights: requestColWeights,
          },
        ],
      },
    ],
  };
}

function exportRequestsToPdfFile({
  rows,
  baselineFilter,
  sourceFilter,
  statusFilter,
  search,
  selectedDoctorLabel,
  showDoctorColumn,
}: {
  rows: ClinicAdminPatientRequestListItem[];
  baselineFilter: PatientBaselineFilter;
  sourceFilter: SourceFilter;
  statusFilter: StatusGroupFilter;
  search: string;
  selectedDoctorLabel: string;
  showDoctorColumn: boolean;
}) {
  const report = buildRequestsExportReport({
    rows,
    baselineFilter,
    sourceFilter,
    statusFilter,
    search,
    selectedDoctorLabel,
    showDoctorColumn,
  });

  downloadReportPdf(report, exportFilename());
}

async function fetchAllRequestsForUi(args: {
  familyDoctorId: number | null;
  signal?: AbortSignal;
}) {
  const allItems: ClinicAdminPatientRequestListItem[] = [];
  const limit = 200;
  let offset = 0;

  while (true) {
    const res = await clinicAdminApi.listPatientRequests(
      {
        familyDoctorProfileId: args.familyDoctorId ?? null,
        q: null,
        baseline: "all",
        sourceScope: "all",
        status: "All",
        limit,
        offset,
      },
      args.signal
    );

    const pageItems = Array.isArray(res?.items) ? res.items : [];
    allItems.push(...pageItems);

    const total = Number((res as any)?.paging?.total);
    const hasKnownTotal = Number.isFinite(total) && total >= 0;

    if (pageItems.length === 0) break;
    if (hasKnownTotal && allItems.length >= total) break;
    if (pageItems.length < limit) break;

    offset += pageItems.length;
  }

  return allItems;
}

function getPaginationTokens(
  currentPage: number,
  totalPages: number
): PaginationToken[] {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const tokens: PaginationToken[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) tokens.push("ellipsis");

  for (let page = start; page <= end; page++) {
    tokens.push(page);
  }

  if (end < totalPages - 1) tokens.push("ellipsis");

  tokens.push(totalPages);
  return tokens;
}

function paginationRangeLabel(start: number, end: number, total: number): string {
  if (total === 0) return "0 requests shown";
  if (start === end) {
    return `${start} of ${total} request${total === 1 ? "" : "s"} shown`;
  }

  return `${start}–${end} of ${total} request${total === 1 ? "" : "s"} shown`;
}

function selectedDoctorLabel(
  doctors: FamilyDoctorListItem[],
  selectedFamilyDoctorId: number | null
): string {
  if (selectedFamilyDoctorId === null) return "All doctors";

  const doctor = doctors.find(
    (d) => Number(d.familyDoctorId) === Number(selectedFamilyDoctorId)
  );

  return doctor?.fullName || `Doctor #${selectedFamilyDoctorId}`;
}

export default function NewPatientRequestsPage() {
  const navigate = useNavigate();

  const [doctorsState, setDoctorsState] = useState<LoadState>("idle");
  const [doctorsError, setDoctorsError] = useState<string | null>(null);
  const [doctors, setDoctors] = useState<FamilyDoctorListItem[]>([]);
  const [selectedFamilyDoctorId, setSelectedFamilyDoctorId] = useState<
    number | null
  >(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<ClinicAdminPatientRequestListItem[]>([]);
  const [search, setSearch] = useState("");

  const [baselineFilter, setBaselineFilter] =
    useState<PatientBaselineFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusGroupFilter>("all");

  const [sortState, setSortState] = useState<SortState>({
    key: "submitted",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const showDoctorColumn = selectedFamilyDoctorId === null;

  const listSeqRef = useRef(0);
  const listAbortRef = useRef<AbortController | null>(null);

  async function loadDoctors() {
    setDoctorsState("loading");
    setDoctorsError(null);

    try {
      const env = await clinicAdminApi.listFamilyDoctors();
      const sorted = [...(env.items || [])].sort((a, b) =>
        (a.fullName || "").localeCompare(b.fullName || "")
      );
      setDoctors(sorted);
      setDoctorsState("ready");
    } catch (e: any) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e?.message || "Failed to load doctors";
      setDoctorsError(msg);
      setDoctorsState("error");
    }
  }

  async function refreshList() {
    setError(null);
    setLoading(true);

    try {
      listAbortRef.current?.abort();
    } catch {}

    const ac = new AbortController();
    listAbortRef.current = ac;

    const seq = ++listSeqRef.current;

    try {
      const data = await fetchAllRequestsForUi({
        familyDoctorId: selectedFamilyDoctorId,
        signal: ac.signal,
      });

      if (seq !== listSeqRef.current) return;

      setItems(data || []);
    } catch (e: any) {
      if (e?.name === "AbortError") return;

      const msg =
        e instanceof ApiError
          ? e.message
          : e?.message || "Failed to load requests";
      setError(msg);
    } finally {
      if (seq === listSeqRef.current) setLoading(false);
    }
  }

  function updateSort(key: SortKey) {
    setSortState((current) => {
      if (current.key !== key) {
        return {
          key,
          direction: key === "submitted" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  }

  function openRequestDetails(requestId: number) {
    if (!Number.isFinite(Number(requestId)) || Number(requestId) <= 0) return;
    navigate(`/portal/clinic-admin/patient-requests/${requestId}`);
  }

  useEffect(() => {
    loadDoctors();
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamilyDoctorId]);

  useEffect(() => {
    return () => {
      try {
        listAbortRef.current?.abort();
      } catch {}
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedFamilyDoctorId,
    sourceFilter,
    baselineFilter,
    statusFilter,
    search,
  ]);

  const sourceCounts = useMemo(() => {
    let publicCount = 0;
    let navigatorCount = 0;

    items.forEach((item) => {
      if (sourceKey(item) === "navigator") {
        navigatorCount += 1;
      } else {
        publicCount += 1;
      }
    });

    return {
      all: items.length,
      public: publicCount,
      navigator: navigatorCount,
    };
  }, [items]);

  const sourceFilteredItems = useMemo(() => {
    return items.filter((item) => sourceMatchesFilter(item, sourceFilter));
  }, [items, sourceFilter]);

  const baselineCounts = useMemo(() => {
    let unattached = 0;
    let attached = 0;

    sourceFilteredItems.forEach((item) => {
      const baseline = patientBaselineKey(item);
      if (baseline === "unattached") unattached += 1;
      if (baseline === "attached") attached += 1;
    });

    return {
      all: sourceFilteredItems.length,
      unattached,
      attached,
    };
  }, [sourceFilteredItems]);

  const primaryFilteredItems = useMemo(() => {
    return sourceFilteredItems.filter((item) =>
      baselineMatchesFilter(item, baselineFilter)
    );
  }, [sourceFilteredItems, baselineFilter]);

  const statusCounts = useMemo<StatusCounts>(() => {
    const counts: StatusCounts = {
      all: primaryFilteredItems.length,
      submittedReview: 0,
      waitlisted: 0,
      invited: 0,
      attached: 0,
      closed: 0,
    };

    primaryFilteredItems.forEach((item) => {
      const group = statusGroupForRequest(item);
      counts[group] = (counts[group] || 0) + 1;
    });

    return counts;
  }, [primaryFilteredItems]);

  const filtered = useMemo(() => {
    const filteredItems = primaryFilteredItems.filter((item) => {
      if (!statusMatchesFilter(item, statusFilter)) return false;
      return requestMatchesSearch(item, search);
    });

    return sortRequests(filteredItems, sortState);
  }, [primaryFilteredItems, statusFilter, search, sortState]);

  const totalMatching = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalMatching / PAGE_SIZE));
  const activePage = Math.min(Math.max(1, currentPage), totalPages);
  const rangeStart =
    totalMatching === 0 ? 0 : (activePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(activePage * PAGE_SIZE, totalMatching);
  const paginatedItems = filtered.slice(
    (activePage - 1) * PAGE_SIZE,
    activePage * PAGE_SIZE
  );
  const paginationTokens = getPaginationTokens(activePage, totalPages);
  const showPaginationControls = totalMatching > PAGE_SIZE;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const selectedDoctorText = useMemo(() => {
    return selectedDoctorLabel(doctors, selectedFamilyDoctorId);
  }, [doctors, selectedFamilyDoctorId]);

  const hasActiveFilters =
    selectedFamilyDoctorId !== null ||
    sourceFilter !== "all" ||
    baselineFilter !== "all" ||
    statusFilter !== "all" ||
    search.trim().length > 0;

  return (
    <div className="ocf-requests-page ca-patient-requests-page">
      <div className="ocf-requests-header">
        <div>
          <div className="ocf-page-eyebrow">Clinic intake queue</div>
          <h1 className="ocf-requests-title">Patient Requests</h1>
          <p className="ocf-requests-subtitle">
            Clinic-wide patient requests submitted through the public site or forwarded
            by a navigator. Use the doctor filter to narrow the queue.
          </p>
        </div>

        <div className="ocf-requests-actions">
          <button
            className="ocf-btn ocf-btn--secondary"
            type="button"
            onClick={() =>
              exportRequestsToPdfFile({
                rows: filtered,
                baselineFilter,
                sourceFilter,
                statusFilter,
                search,
                selectedDoctorLabel: selectedDoctorText,
                showDoctorColumn,
              })
            }
            disabled={loading || filtered.length === 0}
          >
            Export
          </button>

          <button
            className="ocf-btn ocf-btn--secondary"
            type="button"
            onClick={refreshList}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="ocf-card ocf-inbox-card">
        <div className="ocf-inbox-toolbar">
          <div>
            <h2 className="ocf-inbox-title">Requests</h2>
            <p className="ocf-inbox-subtitle">
              {filtered.length} of {items.length} request
              {items.length === 1 ? "" : "s"} shown · {selectedDoctorText}
            </p>
          </div>

          <div className="ocf-inbox-search-wrap">
            <svg
              className="ocf-search-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M20 20L16.65 16.65"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              className="ocf-input ocf-inbox-search"
              placeholder="Search patient name, request number, postal code, or doctor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search patient requests"
            />
          </div>
        </div>

        <div className="ocf-primary-filter-row" aria-label="Primary filters">
          <label className="ocf-filter-select-card ocf-filter-select-card--doctor">
            <span className="ocf-filter-select-label">Doctor</span>
            <select
              className="ocf-select ocf-primary-select"
              value={
                selectedFamilyDoctorId === null
                  ? ""
                  : String(selectedFamilyDoctorId)
              }
              onChange={(e) => {
                const value = e.target.value;
                setSelectedFamilyDoctorId(value ? Number(value) : null);
              }}
              disabled={doctorsState === "loading"}
            >
              <option value="">All doctors</option>
              {doctors.map((doctor) => (
                <option
                  key={doctor.familyDoctorId}
                  value={String(doctor.familyDoctorId)}
                >
                  {doctor.fullName || `Doctor #${doctor.familyDoctorId}`}
                </option>
              ))}
            </select>
          </label>

          <label className="ocf-filter-select-card ocf-filter-select-card--source">
            <span className="ocf-filter-select-label">Source</span>
            <select
              className="ocf-select ocf-primary-select"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
            >
              <option value="all">All ({sourceCounts.all})</option>
              <option value="public">Public ({sourceCounts.public})</option>
              <option value="navigator">
                Navigator ({sourceCounts.navigator})
              </option>
            </select>
          </label>

          <label className="ocf-filter-select-card ocf-filter-select-card--baseline">
            <span className="ocf-filter-select-label">Patient Baseline</span>
            <select
              className="ocf-select ocf-primary-select"
              value={baselineFilter}
              onChange={(e) =>
                setBaselineFilter(e.target.value as PatientBaselineFilter)
              }
            >
              <option value="all">All ({baselineCounts.all})</option>
              <option value="unattached">
                Unattached — MOH ({baselineCounts.unattached})
              </option>
              <option value="attached">
                Attached / Switching ({baselineCounts.attached})
              </option>
            </select>
          </label>
        </div>

        {doctorsState === "error" ? (
          <div className="ca-requests-inline-error">{doctorsError}</div>
        ) : null}

        <div
          className="ocf-filter-row ocf-status-filter-row"
          aria-label="Status filters"
        >
          {(
            [
              "all",
              "submittedReview",
              "waitlisted",
              "invited",
              "attached",
              "closed",
            ] as StatusGroupFilter[]
          ).map((filter) => (
            <button
              key={filter}
              className={`ocf-filter-chip ${
                statusFilter === filter ? "is-active" : ""
              }`}
              type="button"
              onClick={() => setStatusFilter(filter)}
            >
              {statusGroupLabel(filter)} <span>{statusCounts[filter]}</span>
            </button>
          ))}
        </div>

        {hasActiveFilters ? (
          <div className="ocf-active-filter-row">
            <span>
              Filters active
              {selectedFamilyDoctorId !== null
                ? ` · Doctor: ${selectedDoctorText}`
                : ""}
              {sourceFilter !== "all"
                ? ` · Source: ${sourceFilterLabel(sourceFilter)}`
                : ""}
              {baselineFilter !== "all"
                ? ` · Baseline: ${patientBaselineFilterLabel(baselineFilter)}`
                : ""}
              {statusFilter !== "all"
                ? ` · Status: ${statusGroupLabel(statusFilter)}`
                : ""}
              {search.trim() ? ` · Search: “${search.trim()}”` : ""}
            </span>

            <button
              className="ocf-link-button"
              type="button"
              onClick={() => {
                setSelectedFamilyDoctorId(null);
                setSourceFilter("all");
                setBaselineFilter("all");
                setStatusFilter("all");
                setSearch("");
                setCurrentPage(1);
              }}
            >
              Clear filters
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="ocf-inbox-loading">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                width="100%"
                height="42px"
                borderRadius="12px"
              />
            ))}
          </div>
        ) : error ? (
          <div className="ocf-error">
            {error}
            <div className="ocf-error-retry">
              <button className="ocf-btn" type="button" onClick={refreshList}>
                Try again
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="ocf-inbox-empty">
            <div className="ocf-inbox-empty__icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7H20M4 12H14M4 17H10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="18" cy="17" r="3" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className="ocf-inbox-empty__title">
              No requests match the current filters.
            </div>
            <div className="ocf-empty-hint">
              Try changing the doctor, source, patient baseline, status, or search
              filter.
            </div>
          </div>
        ) : (
          <div className="ocf-inbox-list-panel">
          <div className="ocf-inbox-table-wrap">
            <table className="ocf-inbox-table">
              <thead>
                <tr>
                  <th
                    className={
                      sortState.key === "requestId" ? "is-sorted" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => updateSort("requestId")}
                    >
                      {sortButtonLabel("#", "requestId", sortState)}
                    </button>
                  </th>
                  <th
                    className={sortState.key === "name" ? "is-sorted" : undefined}
                  >
                    <button type="button" onClick={() => updateSort("name")}>
                      {sortButtonLabel("Name", "name", sortState)}
                    </button>
                  </th>
                  <th
                    className={
                      sortState.key === "submitted" ? "is-sorted" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => updateSort("submitted")}
                    >
                      {sortButtonLabel("Submitted", "submitted", sortState)}
                    </button>
                  </th>
                  {showDoctorColumn ? (
                    <th
                      className={
                        sortState.key === "doctor" ? "is-sorted" : undefined
                      }
                    >
                      <button
                        type="button"
                        onClick={() => updateSort("doctor")}
                      >
                        {sortButtonLabel("Doctor", "doctor", sortState)}
                      </button>
                    </th>
                  ) : null}
                  <th
                    className={
                      sortState.key === "source" ? "is-sorted" : undefined
                    }
                  >
                    <button type="button" onClick={() => updateSort("source")}>
                      {sortButtonLabel("Source", "source", sortState)}
                    </button>
                  </th>
                  <th
                    className={
                      sortState.key === "patientBaseline" ? "is-sorted" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => updateSort("patientBaseline")}
                    >
                      {sortButtonLabel(
                        "Patient Baseline",
                        "patientBaseline",
                        sortState
                      )}
                    </button>
                  </th>
                  <th
                    className={
                      sortState.key === "priority" ? "is-sorted" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => updateSort("priority")}
                    >
                      {sortButtonLabel("Priority", "priority", sortState)}
                    </button>
                  </th>
                  <th
                    className={
                      sortState.key === "currentStatus" ? "is-sorted" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => updateSort("currentStatus")}
                    >
                      {sortButtonLabel(
                        "Current Status",
                        "currentStatus",
                        sortState
                      )}
                    </button>
                  </th>
                  <th
                    className={
                      sortState.key === "attachmentDate" ? "is-sorted" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => updateSort("attachmentDate")}
                    >
                      {sortButtonLabel(
                        "Attachment Date",
                        "attachmentDate",
                        sortState
                      )}
                    </button>
                  </th>
                  <th
                    className={sortState.key === "tta" ? "is-sorted" : undefined}
                  >
                    <button type="button" onClick={() => updateSort("tta")}>
                      {sortButtonLabel("TTA", "tta", sortState)}
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedItems.map((item) => {
                  const name = patientDisplayName((item as any).patient);
                  const postalCode = patientPostalCode(item);
                  const submitted = submittedAt(item);
                  const attachedAt = attachmentCompletedAt(item);
                  const itemStatus = statusForItem(item);

                  return (
                    <tr
                      key={item.requestId}
                      className="ocf-inbox-row ocf-inbox-row--clickable"
                      tabIndex={0}
                      role="button"
                      aria-label={`View details for request #${item.requestId} — ${name}`}
                      onClick={() => openRequestDetails(item.requestId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openRequestDetails(item.requestId);
                        }
                      }}
                    >
                      <td className="ocf-request-id">#{item.requestId}</td>

                      <td>
                        <div className="ocf-patient-cell">
                          <strong>{name}</strong>
                          <span>{postalCode}</span>
                        </div>
                      </td>

                      <td>
                        <span title={formatDateTime(submitted)}>
                          {formatDate(submitted)}
                        </span>
                      </td>

                      {showDoctorColumn ? (
                        <td>
                          <span className="ocf-doctor-cell">
                            {(item as any)?.familyDoctorName || "—"}
                          </span>
                        </td>
                      ) : null}

                      <td>
                        <span className={sourceClass(item)}>
                          {sourceLabel(item)}
                        </span>
                      </td>

                      <td>
                        <span className={patientBaselineClass(item)}>
                          {patientBaselineLabel(item)}
                        </span>
                      </td>

                      <td>
                        <span className={priorityClass(item)}>
                          {priorityLabel(item)}
                        </span>
                      </td>

                      <td>
                        <span className={statusPillClass(itemStatus)}>
                          {currentStatusLabel(itemStatus)}
                        </span>
                      </td>

                      <td>
                        <span title={formatDateTime(attachedAt)}>
                          {formatDate(attachedAt)}
                        </span>
                      </td>

                      <td>{formatTta(item)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showPaginationControls ? (
            <nav
              className="ocf-inbox-pagination"
              aria-label="Patient requests pagination"
            >
              <div className="ocf-inbox-pagination__summary">
                {paginationRangeLabel(rangeStart, rangeEnd, totalMatching)}
              </div>

              <div className="ocf-inbox-pagination__controls">
                <button
                  className="ocf-pagination-btn"
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={activePage <= 1}
                  aria-label="Previous page"
                >
                  Previous
                </button>

                <div className="ocf-inbox-pagination__pages" role="group" aria-label="Pages">
                  {paginationTokens.map((token, index) =>
                    token === "ellipsis" ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="ocf-pagination-ellipsis"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={token}
                        className={`ocf-pagination-btn ocf-pagination-btn--page ${
                          token === activePage ? "is-active" : ""
                        }`}
                        type="button"
                        onClick={() => setCurrentPage(token)}
                        aria-label={`Page ${token}`}
                        aria-current={token === activePage ? "page" : undefined}
                      >
                        {token}
                      </button>
                    )
                  )}
                </div>

                <button
                  className="ocf-pagination-btn"
                  type="button"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={activePage >= totalPages}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </nav>
          ) : null}
          </div>
        )}

        <div className="ocf-inbox-footnote">
          Patient Baseline reflects the patient’s status before applying, based
          on the MOH-style reason captured at intake. Clinic Admin can complete
          attachment or close post-invitation outcomes from the request details
          page, but initial clinical decisions remain provider-controlled.
        </div>
      </div>
    </div>
  );
}